import { useCalculusFilters } from '@retail/calculus/context';
import { filterConditions } from '@retail/calculus/utils';
import { Button, CircularProgress, Fab, Stack } from '@mui/material';
import { Close, Edit, Save } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  AddButton,
  DensityMenu,
  ErrorFallback,
  FilterChipContainer,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
  ToggleSwitch,
} from '@retail/components';
import { useDisclosure } from '@shared/hooks';
import { ConditionsNew } from '@retail/calculus/types';
import { useCallback, useMemo, useState } from 'react';
import { GridDensity } from '@mui/x-data-grid-premium';
import { HierarchyLevels } from '@retail/products/types';
import {
  buildDiscountMatrix,
  ConditionCount,
  DiscountMatrixEdits,
  DiscountMatrixTable,
  getDiscountMatrixChanges,
} from '@retail/calculus/components';
import {
  useFetchConditions,
  useMutatePostCustomerMarkupConditions,
} from '@retail/calculus/data-access';
import { useToast } from '@retail/hooks';
import {
  useCustomerAssortments,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import {
  brandLogo,
  DATA_GRID_ID,
  factorFromDiscountPercent,
  getInitialDataGridDensityFromLocalStorage,
  getInitialDataGridShowDatesFromLocalStorage,
  getISO_8601Date,
  saveDataGridDensityToLocalStorage,
  saveDataGridShowDatesToLocalStorage,
} from '@retail/utils';
import { calculusFilters } from '@retail/products/utils';
import { useFetchAllSuppliers } from '@retail/products/data-access';
import { useFilters } from '@retail/products/hooks';
import { SupplierBase } from '@retail/suppliers/types';
import { CalculusFilters } from './Filters';
import CreateCustomerMarkupConditionDialog from './CustomerMarkupConditions/Create/CreateCustomerMarkupConditionDialog';

export function DiscountMatrixContainer() {
  const t = useCalculusTFunction();
  const { assortmentId, selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const locationCustomerAssortments = useCustomerAssortments();
  const toDraftAssortmentId = (conditionAssortmentId: number) =>
    locationCustomerAssortments.find(
      ({ assortmentId: id, draftAssortmentId }) =>
        id === conditionAssortmentId || draftAssortmentId === conditionAssortmentId
    )?.draftAssortmentId ?? conditionAssortmentId;
  const { data: supplierDtos = [] } = useFetchAllSuppliers(
    { assortmentId: selectedOrgUnitAssortment.draftAssortmentId },
    { suspense: false }
  );
  const suppliers = useMemo(() => supplierDtos.map(SupplierBase.fromSupplierDTO), [supplierDtos]);

  const { filters: supplierFilter, setFilters: setSupplierFilter } = useFilters(['supplierNumber']);
  const selectedSupplier = useMemo(
    () =>
      suppliers.find(
        (supplier) => supplier.supplierNumber.toString() === supplierFilter.supplierNumber
      ) ?? null,
    [supplierFilter.supplierNumber, suppliers]
  );

  const { userCanCreate } = usePermissionsForCurrentUser();
  const userCanCreateDiscounts = userCanCreate('PRICE_CALCULATION__CUSTOMER_MARKUP');

  const [density, setDensity] = useState<GridDensity>(() =>
    getInitialDataGridDensityFromLocalStorage(DATA_GRID_ID.CALCULUS_DISCOUNT_MATRIX_TABLE)
  );
  const changeDensity = (newDensity: GridDensity) => {
    setDensity(newDensity);
    saveDataGridDensityToLocalStorage(DATA_GRID_ID.CALCULUS_DISCOUNT_MATRIX_TABLE, newDensity);
  };

  const [showDates, setShowDates] = useState(() =>
    getInitialDataGridShowDatesFromLocalStorage(DATA_GRID_ID.CALCULUS_DISCOUNT_MATRIX_TABLE)
  );
  const changeShowDates = (newShowDates: boolean) => {
    setShowDates(newShowDates);
    saveDataGridShowDatesToLocalStorage(DATA_GRID_ID.CALCULUS_DISCOUNT_MATRIX_TABLE, newShowDates);
  };

  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);
  const { isOpen: isCreateOpen, onToggle: _toggleCreateDialog } = useDisclosure();
  const toggleCreateDialog = userCanCreateDiscounts ? _toggleCreateDialog : undefined;

  const { productGroups, discountGroups, filters } = useCalculusFilters({
    supplierNumber: selectedSupplier?.supplierNumber,
    conditionType: 'CUSTOMER_MARKUP',
  });
  const { selectedBrand } = useUserBrands();

  const {
    data: conditionsDTO = [],
    isFetching,
    error,
  } = useFetchConditions({
    assortmentId,
    supplierNumber: selectedSupplier?.supplierNumber,
    productGroupNumber: filters.productGroupNumber ?? undefined,
    discountGroupCode: filters.discountGroupCode ?? undefined,
    conditionType: 'CUSTOMER_MARKUP',
    conditionState: filters.conditionState,
    assortmentType: filters.assortmentType,
  });
  const conditions = useMemo(
    () =>
      conditionsDTO.map((cond) =>
        ConditionsNew.fromDto(cond, {
          name: selectedBrand.name,
          id: selectedBrand.brandId,
          imgUrl: brandLogo(selectedBrand.brandId).logo,
        })
      ),
    [conditionsDTO, selectedBrand.brandId, selectedBrand.name]
  );

  const filteredConditions = useMemo(
    () =>
      filterConditions(conditions, {
        hierarchyLevel: filters.hierarchyLevel,
        itemSearchQuery: filters.itemSearchQuery,
      }),
    [conditions, filters.hierarchyLevel, filters.itemSearchQuery]
  );

  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState<DiscountMatrixEdits>({});
  const onEdit = useCallback(
    (cellKey: string, value: string) => setEdits((prev) => ({ ...prev, [cellKey]: value })),
    []
  );
  const toggleEditMode = () => {
    setEdits({});
    setEditMode((prev) => !prev);
  };

  const { changes, hasInvalid } = useMemo(
    () => getDiscountMatrixChanges(buildDiscountMatrix(filteredConditions), edits),
    [edits, filteredConditions]
  );

  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: postCustomerMarkupConditions, isPending: isSaving } =
    useMutatePostCustomerMarkupConditions({ assortmentIds: [] });

  const saveChanges = async () => {
    const validFrom = getISO_8601Date(new Date());
    const changesByAssortment = new Map<number, typeof changes>();
    changes.forEach((change) =>
      changesByAssortment.set(change.assortmentId, [
        ...(changesByAssortment.get(change.assortmentId) ?? []),
        change,
      ])
    );

    try {
      await Promise.all(
        Array.from(changesByAssortment, ([changeAssortmentId, assortmentChanges]) =>
          postCustomerMarkupConditions({
            urlVariables: { assortmentIds: [toDraftAssortmentId(changeAssortmentId)] },
            body: assortmentChanges.map(({ row, discountPercent }) => ({
              customerMarkup: { factor: factorFromDiscountPercent(discountPercent) },
              binding: row.getBindingLevel(),
              validFrom,
            })),
          })
        )
      );
      toast.success(t('calculus.actions.conditionsCreateInDrawer'));
      setEdits({});
      setEditMode(false);
    } catch (err) {
      console.error(err);
      toast.error(t('calculus.discounts.saveError'));
    } finally {
      queryClient.invalidateQueries({ queryKey: ['conditions'] });
      queryClient.invalidateQueries({ queryKey: ['draft-conditions'] });
    }
  };

  const availableHierarchyLevels: HierarchyLevels[] | undefined = useMemo(
    () => Array.from(new Set(conditionsDTO.map((cond) => cond.bindingType))),
    [conditionsDTO]
  );

  const availablePackageTypes = useMemo(
    () =>
      Array.from(new Set(conditionsDTO.map((cond) => cond.bindingTargetItem?.packageType))).filter(
        (type) => !!type
      ) ?? [],
    [conditionsDTO]
  );

  return (
    <Stack flexGrow={1} overflow="auto" position="relative">
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'flex-end' }}>
        <Stack flexDirection="row" alignItems="center" gap={1}>
          {userCanCreateDiscounts && (
            <Button
              variant={editMode ? 'outlined' : 'contained'}
              startIcon={editMode ? <Close /> : <Edit />}
              onClick={toggleEditMode}
              disabled={isSaving}
            >
              {editMode ? t('calculus.cancel') : t('calculus.discounts.editMatrix')}
            </Button>
          )}
          {toggleCreateDialog && (
            <AddButton onClick={toggleCreateDialog}>{t('calculus.discounts.create')}</AddButton>
          )}

          <ToggleFiltersButton
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            buttonHeight={50}
            sx={{ mb: -1.1 }}
          />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack flexGrow={1} flexDirection="row">
        <Stack flexGrow={1} pt={1} justifyContent={error ? 'center' : undefined}>
          {error ? (
            <ErrorFallback message={error.payload.message ?? error.payload.detail ?? error.text} />
          ) : (
            <>
              <Stack flexDirection="row" justifyContent="space-between" px={2} height={32}>
                <FilterChipContainer
                  allowedKeys={['supplierNumber', ...calculusFilters]}
                  filterOptions={{ productGroups, discountGroups, suppliers }}
                />
                <Stack flexDirection="row" alignItems="center" gap={1}>
                  <ToggleSwitch
                    toggle={showDates}
                    setToggle={changeShowDates}
                    text={t('calculus.discounts.showDates')}
                  />
                  <DensityMenu density={density} onDensityChange={changeDensity} />
                  <ConditionCount totalCount={filteredConditions.length} />
                </Stack>
              </Stack>

              <DiscountMatrixTable
                conditions={filteredConditions}
                orgUnitName={selectedOrgUnitAssortment.name}
                loading={isFetching}
                density={density}
                showDates={showDates}
                editMode={editMode}
                edits={edits}
                onEdit={onEdit}
              />
            </>
          )}
        </Stack>
        <CalculusFilters
          productGroups={productGroups}
          discountGroups={discountGroups}
          conditionState={filters.conditionState}
          assortmentType={filters.assortmentType}
          productGroupNumber={filters.productGroupNumber}
          discountGroupCode={filters.discountGroupCode}
          itemSearchQuery={filters.itemSearchQuery}
          selectedHierarchyLevel={filters.hierarchyLevel}
          availableHierarchyLevels={availableHierarchyLevels}
          availablePackageTypes={availablePackageTypes}
          supplierNumber={selectedSupplier?.supplierNumber}
          supplierSearch={{
            suppliers,
            selectedSupplier,
            selectSupplierById: (supplierNumber) =>
              setSupplierFilter({
                supplierNumber: supplierNumber ? supplierNumber.toString() : undefined,
              }),
          }}
          isLoadingContent={isFetching}
          showFilters={showFilters}
        />
      </Stack>

      {editMode && (
        <Fab
          color="primary"
          variant="extended"
          onClick={saveChanges}
          disabled={changes.length === 0 || hasInvalid || isSaving}
          sx={{ position: 'absolute', bottom: 24, right: 24, zIndex: 50, gap: 1 }}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          {t('calculus.discounts.saveChanges', { count: changes.length })}
        </Fab>
      )}

      {isCreateOpen && toggleCreateDialog && (
        <CreateCustomerMarkupConditionDialog
          open
          target={{ type: 'chooseAssortmentOrSupplier' }}
          title={t('calculus.discounts.create')}
          valueMode="discount"
          onClose={toggleCreateDialog}
        />
      )}
    </Stack>
  );
}
